import React from 'react';
import { motion } from 'framer-motion';
import { Award, Star, Zap, BarChart3, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../config/urls';

interface QuizCompletionCtaProps {
  className?: string;
  prospect?: {
    token?: string;
    quiz_fait?: boolean;
    quiz_commence?: boolean;
  };
}

const QuizCompletionCta: React.FC<QuizCompletionCtaProps> = ({
  className = '',
  prospect
}) => {
  const navigate = useNavigate();

  const handleQuizClick = () => {
    const validToken = prospect?.token && prospect.token.trim() !== '' ? prospect.token : undefined;
    navigate(ROUTES.QUIZ(validToken));
  };
  return (
    <motion.div 
      className={`relative overflow-hidden rounded-xl my-16 bg-white shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-md ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {/* Top brand border (animated orange) */}
      <div className="p-1 animate-pulse" style={{ backgroundColor: '#eb811c' }}></div>
      
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50/30 via-transparent to-orange-100/20 pointer-events-none" />
      
      {/* Decorative elements */}
      <motion.div 
        className="absolute right-0 top-0 -translate-y-1/4 translate-x-1/4 transform rounded-full bg-gradient-to-br from-orange-400/20 to-orange-500/20 blur-3xl filter"
        animate={{ 
          scale: [1, 1.05, 1],
          opacity: [0.3, 0.5, 0.3]
        }}
        transition={{ 
          duration: 4, 
          repeat: Infinity,
          repeatType: "reverse" 
        }}
        style={{ width: '25rem', height: '15rem' }}
      />
      
      {/* Floating particles */}
      <motion.div 
        className="absolute top-8 left-1/4 w-2 h-2 bg-orange-400/60 rounded-full"
        animate={{ 
          y: [0, -10, 0],
          opacity: [0.6, 1, 0.6]
        }}
        transition={{ 
          duration: 3, 
          repeat: Infinity,
          repeatType: "reverse"
        }}
      />
      <motion.div 
        className="absolute bottom-8 right-1/3 w-1.5 h-1.5 bg-orange-500/70 rounded-full"
        animate={{ 
          y: [0, -8, 0],
          opacity: [0.5, 0.9, 0.5]
        }}
        transition={{ 
          duration: 4, 
          repeat: Infinity,
          repeatType: "reverse",
          delay: 1
        }}
      />
      
      {/* Content container */}
      <div className="relative z-10 p-8 lg:p-10">
        <div className="mx-auto max-w-2xl text-center">
          {/* Icon */}
          <motion.div 
            className="flex items-center justify-center mx-auto mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <motion.div 
              className="flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-lg"
              whileHover={{ scale: 1.05, rotate: 5 }}
              animate={{ 
                boxShadow: [
                  "0 4px 6px -1px rgba(251, 146, 60, 0.3)",
                  "0 8px 12px -2px rgba(251, 146, 60, 0.4)",
                  "0 4px 6px -1px rgba(251, 146, 60, 0.3)"
                ]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
                repeatType: "reverse" 
              }}
            >
              <Zap className="w-6 h-6" />
            </motion.div>
          </motion.div>
          
          {/* Title */}
          <motion.h3
            className="mb-4 text-2xl sm:text-3xl font-bold text-gray-900"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            {prospect?.quiz_fait
              ? "Votre SmartIndex™ vous attend 🎯"
              : "Quelques pistes pour booster votre présence digitale"}
          </motion.h3>

          {/* Message */}
          <motion.p
            className="mb-8 text-gray-600"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            {prospect?.quiz_fait
              ? "Votre SmartIndex™ personnalisé et votre plan d'action sont prêts. Découvrez exactement où vous perdez du chiffre d'affaires 💰"
              : prospect?.quiz_commence
                ? "Plus que quelques questions pour débloquer votre SmartIndex™ complet et découvrir les opportunités que vous ratez actuellement 🚀"
                : "7 questions rapides pour connaître votre vraie maturité digitale (et les opportunités de revenus que vous laissez passer) 💡"}
          </motion.p>
          
          {/* Features badges */}
          <motion.div 
            className="flex flex-wrap justify-center gap-3 mb-8"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
          >
            {[
              { icon: <Star className="w-4 h-4" />, text: "7 questions rapides", color: "orange" },
              { icon: <BarChart3 className="w-4 h-4" />, text: "Résultats instantanés", color: "orange" },
              { icon: <Award className="w-4 h-4" />, text: "Conseils personnalisés", color: "orange" }
            ].map((feature, index) => (
              <motion.div 
                key={index}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-gray-100 text-sm font-medium text-gray-700"
                whileHover={{ 
                  scale: 1.05, 
                  boxShadow: "0 4px 12px -2px rgba(0, 0, 0, 0.1)" 
                }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8 + index * 0.1, duration: 0.4 }}
              >
                <div className="text-orange-600">
                  {feature.icon}
                </div>
                <span>{feature.text}</span>
              </motion.div>
            ))}
          </motion.div>
          
          {/* Button container */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.5 }}
            className="relative flex justify-center"
          >
            {/* Glow effect behind button */}
            <motion.div 
              className="absolute inset-0 bg-white rounded-2xl blur-lg opacity-20"
              animate={{ 
                scale: [1, 1.05, 1],
                opacity: [0.2, 0.3, 0.2]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
                repeatType: "reverse" 
              }}
            />
            
            {/* CTA Button */}
            <motion.button
              onClick={handleQuizClick}
              className="group relative flex items-center justify-center gap-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold text-lg py-4 px-8 rounded-2xl shadow-lg transition-all duration-300 mx-auto"
              whileHover={{
                scale: 1.02,
                boxShadow: "0 20px 25px -5px rgba(251, 146, 60, 0.3), 0 10px 10px -5px rgba(251, 146, 60, 0.1)"
              }}
              whileTap={{ scale: 0.98 }}
            >
              <Zap className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
              <span>
                {prospect?.quiz_fait
                  ? "Voir mon SmartIndex™ 🔓"
                  : prospect?.quiz_commence
                    ? "Débloquer mon score 🎯"
                    : "Découvrir mon potentiel 🚀"}
              </span>
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              
              {/* Button shine effect */}
              <motion.div 
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent rounded-2xl"
                initial={{ x: "-100%" }}
                animate={{ x: "100%" }}
                transition={{ 
                  duration: 2, 
                  repeat: Infinity,
                  repeatDelay: 3,
                  ease: "easeInOut"
                }}
              />
            </motion.button>
          </motion.div>
          
          {/* Trust indicators */}
          <motion.div 
            className="flex justify-center items-center mt-6 space-x-6 text-sm text-gray-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.5 }}
          >
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
              Sans engagement
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-orange-600 rounded-full"></span>
              Données sécurisées
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
              Résultats immédiats
            </span>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default QuizCompletionCta;
